import { SHADOW_REALM_TIMEZONE } from "@sitescene/constant";
import { monthKeyInZone } from "@sitescene/shared";
import { type VlmCostByGroup, VlmCostGroup } from "@sitescene/types";

/** "YYYY-MM" of a date, in Mountain Time — the cost report's month boundaries. */
export const monthKey = (date: Date): string =>
	monthKeyInZone(date, SHADOW_REALM_TIMEZONE);

/**
 * A "YYYY-MM" month `delta` months on, as UTC midnight on its 1st. Only for
 * calendar math on the key, not a window boundary.
 */
const monthStart = (key: string, delta = 0): Date => {
	const year = Number(key.slice(0, 4));
	const month = Number(key.slice(5, 7));
	return new Date(Date.UTC(year, month - 1 + delta, 1));
};

/** The "YYYY-MM" month `delta` months away from `key`. */
export const shiftMonth = (key: string, delta: number): string => {
	// Calendar math only: UTC midnight on the 1st names its own month in UTC
	return monthStart(key, delta).toISOString().slice(0, 7);
};

/** "September 2026" for "2026-09". */
export const formatMonth = (key: string): string => {
	return monthStart(key).toLocaleDateString(undefined, {
		month: "long",
		timeZone: "UTC",
		year: "numeric",
	});
};

const usd = new Intl.NumberFormat("en-US", {
	currency: "USD",
	maximumFractionDigits: 2,
	minimumFractionDigits: 2,
	style: "currency",
});

/** Dollars to the cent; a real but sub-cent amount reads "<$0.01", not "$0.00". */
export const formatUsd = (value: number): string =>
	value > 0 && value < 0.005 ? "<$0.01" : usd.format(value);

const unitUsd = new Intl.NumberFormat("en-US", {
	currency: "USD",
	maximumFractionDigits: 4,
	minimumFractionDigits: 2,
	style: "currency",
});

/**
 * A per-unit cost to a hundredth of a cent — one clip usually costs a fraction
 * of a cent, which `formatUsd` would flatten to "<$0.01" for every sensor.
 */
export const formatUnitUsd = (value: number): string =>
	value > 0 && value < 0.00005 ? "<$0.0001" : unitUsd.format(value);

/** Share of a total as a whole-number percent, "<1%" when small but real. */
export const formatShare = (value: number, total: number): string => {
	if (total <= 0) return "—";
	const share = (value / total) * 100;
	return share > 0 && share < 1 ? "<1%" : `${Math.round(share)}%`;
};

/** "Sep 1, 12:00 AM – Sep 30, 11:59 PM MT" */
export const formatWindow = (from: string, to: string): string => {
	const options: Intl.DateTimeFormatOptions = {
		day: "numeric",
		hour: "numeric",
		hour12: true,
		minute: "2-digit",
		month: "short",
		timeZone: SHADOW_REALM_TIMEZONE,
	};
	const format = (value: string) =>
		new Date(value).toLocaleString("en-US", options);
	return `${format(from)} – ${format(to)} MT`;
};

/** Cost groups in display order, with their column labels. */
export const COST_GROUPS: readonly { group: VlmCostGroup; label: string }[] = [
	{ group: VlmCostGroup.StageOne, label: "Stage 1" },
	{ group: VlmCostGroup.FollowUp, label: "Follow-up" },
	{ group: VlmCostGroup.Verification, label: "Verification" },
	{ group: VlmCostGroup.StaffReanalysis, label: "Staff reanalysis" },
	{ group: VlmCostGroup.Scene, label: "Scene" },
	{ group: VlmCostGroup.Other, label: "Other" },
];

/** The groups with any spend, so empty columns don't crowd the tables. */
export const activeCostGroups = (costByGroup: VlmCostByGroup) =>
	COST_GROUPS.filter(({ group }) => costByGroup[group] > 0);
